'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from '@/i18n';

export type HeroShowcaseSlide = {
  id?: string;
  title: string;
  subtitle: string;
  badge_text: string;
  image_url?: string | null;
};

function clampInt(value: unknown, min: number, max: number, fallback: number) {
  const n = typeof value === 'number' ? value : parseInt(String(value ?? ''), 10);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(n)));
}

function idxWrap(index: number, count: number) {
  if (count <= 0) return 0;
  return (index % count + count) % count;
}

function titleForWindow(slide: HeroShowcaseSlide, locale: string) {
  const key = String(slide.badge_text || '').toLowerCase();
  if (key.includes('client')) return locale === 'ar' ? 'شركات العملاء' : 'Client Companies';
  if (key.includes('renew') || key.includes('expiry')) return locale === 'ar' ? 'متعقب الانتهاء' : 'Expiry Tracker';
  if (key.includes('finance') || key.includes('invoice') || key.includes('payment')) return locale === 'ar' ? 'الفواتير والمدفوعات' : 'Invoices & Payments';
  if (key.includes('portal')) return locale === 'ar' ? 'بوابة العميل' : 'Client Portal';
  return locale === 'ar' ? 'بروفلو' : 'ProFlow';
}

function ClientCompaniesMock({ locale }: { locale: string }) {
  const rows = [
    { name: 'DC Enterprises', licence: '325456', emirate: 'Sharjah', jurisdiction: 'DMCC', expiry: '16/06/2025', status: locale === 'ar' ? 'نشط' : 'Active' },
    { name: 'Alin Holdings', licence: '256789', emirate: 'Dubai', jurisdiction: locale === 'ar' ? 'البر الرئيسي' : 'Mainland', expiry: '10/06/2025', status: locale === 'ar' ? 'نشط' : 'Active' },
    { name: 'Blue Sky Group', licence: '478291', emirate: locale === 'ar' ? 'أبوظبي' : 'Abu Dhabi', jurisdiction: 'ADGM', expiry: '18/06/2025', status: locale === 'ar' ? 'نشط' : 'Active' },
    { name: 'Nexus Solutions', licence: '498212', emirate: locale === 'ar' ? 'الشارقة' : 'Sharjah', jurisdiction: locale === 'ar' ? 'منطقة سيف' : 'SAIF Zone', expiry: '22/07/2025', status: locale === 'ar' ? 'نشط' : 'Active' },
  ];

  const tHeaderCompany = locale === 'ar' ? 'الشركة' : 'Company';
  const tHeaderLicence = locale === 'ar' ? 'رقم الرخصة' : 'Licence No';
  const tHeaderEmirate = locale === 'ar' ? 'الإمارة' : 'Emirate';
  const tHeaderJurisdiction = locale === 'ar' ? 'الاختصاص' : 'Jurisdiction';
  const tHeaderExpiry = locale === 'ar' ? 'انتهاء الرخصة' : 'Licence Expiry';
  const tHeaderStatus = locale === 'ar' ? 'الحالة' : 'Status';

  const tTitle = locale === 'ar' ? 'شركات العملاء' : 'Client Companies';
  const tDesc = locale === 'ar' ? 'أدر شركات عملائك وتفاصيلها' : 'Manage your client companies and their details';
  const tAdd = locale === 'ar' ? '+ إضافة شركة' : '+ Add Company';
  const tShowing = locale === 'ar' ? 'يتم عرض 1-4 من 4 شركات' : 'Showing 1-4 of 4 companies';
  const tActivity = locale === 'ar' ? 'موجز النشاط' : 'Activity Feed';
  const tApproval = locale === 'ar' ? 'موافقة الرخصة 325456 قيد الإجراء...' : 'Licence 325456 approval in progress...';

  return (
    <div className="h-full p-4 sm:p-5 flex flex-col">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold text-slate-900">{tTitle}</p>
          <p className="text-xs text-slate-500 mt-0.5">{tDesc}</p>
        </div>
        <div className="h-8 px-3 rounded-lg bg-slate-900 text-white text-xs font-semibold flex items-center">
          {tAdd}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-12 gap-3">
        <div className="col-span-6 h-9 rounded-lg bg-white border border-slate-200 flex items-center px-3">
          <div className="h-2.5 w-24 rounded bg-slate-200" />
        </div>
        <div className="col-span-3 h-9 rounded-lg bg-white border border-slate-200 flex items-center px-3 justify-between">
          <div className="h-2.5 w-16 rounded bg-slate-200" />
          <div className="w-2 h-2 rounded bg-slate-300" />
        </div>
        <div className="col-span-3 h-9 rounded-lg bg-white border border-slate-200 flex items-center px-3 justify-between">
          <div className="h-2.5 w-16 rounded bg-slate-200" />
          <div className="w-2 h-2 rounded bg-slate-300" />
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 overflow-hidden bg-white flex-1 min-h-0">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase">
          <div className="col-span-3">{tHeaderCompany}</div>
          <div className="col-span-2">{tHeaderLicence}</div>
          <div className="col-span-2">{tHeaderEmirate}</div>
          <div className="col-span-2">{tHeaderJurisdiction}</div>
          <div className="col-span-2">{tHeaderExpiry}</div>
          <div className="col-span-1 text-right">{tHeaderStatus}</div>
        </div>
        <div className="divide-y divide-slate-100">
          {rows.map((r) => (
            <div key={r.name} className="grid grid-cols-12 gap-2 px-3 py-2.5 text-xs text-slate-700 items-center">
              <div className="col-span-3 flex items-center gap-2 font-semibold">
                <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-[10px] font-bold">OC</span>
                <span className="truncate">{r.name}</span>
              </div>
              <div className="col-span-2 font-mono text-[11px] text-slate-500">{r.licence}</div>
              <div className="col-span-2 text-slate-500">{r.emirate}</div>
              <div className="col-span-2 text-slate-500">{r.jurisdiction}</div>
              <div className="col-span-2 text-emerald-600 font-semibold">{r.expiry}</div>
              <div className="col-span-1 text-right">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[11px] font-bold">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  {r.status}
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="px-3 py-2 text-[10px] text-slate-500 bg-white border-t border-slate-100">
          {tShowing}
        </div>
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white px-4 py-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-[11px] font-bold text-slate-700">{tActivity}</p>
            <p className="text-[10px] text-slate-500 mt-0.5">{tApproval}</p>
          </div>
          <p className="text-[11px] font-bold text-emerald-700">97%</p>
        </div>
        <div className="mt-3 h-8 rounded-lg bg-white border border-slate-200 overflow-hidden relative">
          <div className="absolute inset-0 opacity-60 bg-[radial-gradient(circle_at_70%_50%,rgba(200,146,42,0.25),transparent_55%)]" />
          <div className="absolute inset-x-3 top-1/2 -translate-y-1/2 h-[2px] rounded-full bg-slate-200" />
          <div className="absolute left-3 right-8 top-1/2 -translate-y-1/2 h-[2px] rounded-full bg-slate-900/70" />
        </div>
      </div>
    </div>
  );
}

function GenericModuleMock({ slide, locale }: { slide: HeroShowcaseSlide; locale: string }) {
  const title = titleForWindow(slide, locale);
  const tItemsLabel1 = locale === 'ar' ? 'الرخصة التجارية' : 'Trade Licence';
  const tItemsLabel2 = locale === 'ar' ? 'التأشيرة' : 'Visa';
  const tItemsLabel3 = locale === 'ar' ? 'مذكرة التأسيس' : 'MOA';
  const tItemsLabel4 = locale === 'ar' ? 'جواز السفر' : 'Passport';
  const tItemsValue1 = locale === 'ar' ? '7 أيام' : '7 days';
  const tItemsValue2 = locale === 'ar' ? '15 يوماً' : '15 days';
  const tItemsValue3 = locale === 'ar' ? '23 يوماً' : '23 days';
  const tItemsValue4 = locale === 'ar' ? '30 يوماً' : '30 days';
  const items = [
    { label: tItemsLabel1, value: tItemsValue1, tone: 'text-amber-600 bg-amber-50 border-amber-100' },
    { label: tItemsLabel2, value: tItemsValue2, tone: 'text-amber-600 bg-amber-50 border-amber-100' },
    { label: tItemsLabel3, value: tItemsValue3, tone: 'text-emerald-700 bg-emerald-50 border-emerald-100' },
    { label: tItemsLabel4, value: tItemsValue4, tone: 'text-slate-600 bg-slate-50 border-slate-200' },
  ];

  const tUpcoming = locale === 'ar' ? 'العناصر والتنبيهات القادمة' : 'Upcoming items and alerts';
  const tExpiring = locale === 'ar' ? 'قريباً من الانتهاء' : 'Expiring Soon';

  return (
    <div className="h-full p-4 sm:p-5 flex flex-col">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-bold text-slate-900">{title}</p>
          <p className="text-xs text-slate-500 mt-0.5">{tUpcoming}</p>
        </div>
        <div className="h-8 w-24 rounded-lg bg-slate-100 border border-slate-200" />
      </div>

      <div className="mt-4 rounded-xl border border-slate-200 bg-white overflow-hidden flex-1 min-h-0">
        <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] font-bold text-slate-500 uppercase">{tExpiring}</div>
        <div className="divide-y divide-slate-100">
          {items.map((it) => (
            <div key={it.label} className="px-3 py-2.5 flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-700">{it.label}</p>
              <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full border ${it.tone}`}>{it.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-3">
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3">
          <div className="h-2.5 w-16 rounded bg-slate-200" />
          <div className="mt-2 h-6 w-10 rounded bg-amber-200" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3">
          <div className="h-2.5 w-20 rounded bg-slate-200" />
          <div className="mt-2 h-6 w-12 rounded bg-slate-900/10" />
        </div>
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-3">
          <div className="h-2.5 w-14 rounded bg-slate-200" />
          <div className="mt-2 h-6 w-10 rounded bg-emerald-200" />
        </div>
      </div>
    </div>
  );
}

function SlideWindow({
  slide,
  showControls,
  onPrev,
  onNext,
  disableControls,
  locale,
}: {
  slide: HeroShowcaseSlide;
  showControls: boolean;
  onPrev: () => void;
  onNext: () => void;
  disableControls: boolean;
  locale: string;
}) {
  const windowTitle = titleForWindow(slide, locale);
  const tPrev = locale === 'ar' ? 'الشريحة السابقة للعرض' : 'Previous showcase slide';
  const tNext = locale === 'ar' ? 'الشريحة التالية للعرض' : 'Next showcase slide';

  return (
    <div className="rounded-2xl border border-sky-300/35 bg-[#eef4ff] overflow-hidden h-full flex flex-col shadow-[0_20px_48px_-30px_rgba(15,23,42,0.45)]">
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-slate-200 bg-[#274a80]">
        <div className="flex items-center gap-2">
          <div className="w-2.5 h-2.5 rounded-full bg-danger/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-warning/80" />
          <div className="w-2.5 h-2.5 rounded-full bg-success/80" />
        </div>
        <div className="ml-3 flex-1 min-w-0">
          <div className="h-6 rounded-lg bg-white/12 border border-sky-200/20 flex items-center px-3">
            <div className="h-2.5 w-28 bg-white/30 rounded" />
          </div>
        </div>

        {showControls && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onPrev}
              disabled={disableControls}
              className="w-8 h-8 rounded-lg bg-[#173153] border border-sky-200/20 text-white hover:bg-[#1d3b63] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              aria-label={tPrev}
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={onNext}
              disabled={disableControls}
              className="w-8 h-8 rounded-lg bg-[#173153] border border-sky-200/20 text-white hover:bg-[#1d3b63] disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
              aria-label={tNext}
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      <div className="relative flex-1 min-h-0 bg-white overflow-hidden">
        <div className="relative h-full w-full">
          <div className="sr-only">{windowTitle}</div>
          {slide.image_url ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={slide.image_url}
              alt={slide.title}
              className="absolute inset-0 w-full h-full object-cover object-top bg-white"
              loading="lazy"
            />
          ) : (
            <div className="h-full">
              {String(slide.badge_text || '').toLowerCase().includes('client') ? (
                <ClientCompaniesMock locale={locale} />
              ) : (
                <GenericModuleMock slide={slide} locale={locale} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function HeroShowcase({
  slides,
  intervalSeconds = 3,
}: {
  slides: HeroShowcaseSlide[];
  intervalSeconds?: number;
}) {
  const { locale } = useTranslation();
  const safeInterval = clampInt(intervalSeconds, 2, 15, 3);
  const intervalMs = safeInterval * 1000;

  const safeSlides = useMemo(() => {
    const list = Array.isArray(slides) ? slides : [];
    const cleaned = list
      .map((s) => ({
        id: s.id,
        title: String(s.title || '').trim(),
        subtitle: String(s.subtitle || '').trim(),
        badge_text: String(s.badge_text || '').trim(),
        image_url: s.image_url ? String(s.image_url) : null,
      }))
      .filter((s) => s.title && s.subtitle);
    return cleaned.length > 0 ? cleaned : [];
  }, [slides]);

  const [paused, setPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const rafRef = useRef<number | null>(null);
  const startRef = useRef<number>(0);

  const count = safeSlides.length;
  const [deck, setDeck] = useState<number[]>([]);
  const [outgoing, setOutgoing] = useState<{ index: number; stage: 0 | 1 } | null>(null);

  const front = deck[0] ?? 0;
  const mid = deck[1] ?? idxWrap(front + 1, count);
  const back = deck[2] ?? idxWrap(front + 2, count);

  useEffect(() => {
    if (count <= 1) { setProgress(0); return; }
    if (paused) { return; }
    startRef.current = performance.now();
    setProgress(0);

    const tick = (now: number) => {
      const elapsed = now - startRef.current;
      const p = Math.min(1, Math.max(0, elapsed / intervalMs));
      setProgress(p);
      if (p < 1) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, [front, paused, intervalMs, count]);

  useEffect(() => {
    if (paused) return;
    if (count <= 1) return;
    const t = window.setTimeout(() => {
      setDeck((prev) => {
        if (count <= 1) return prev;
        const cur = prev.length ? prev : [0, 1, 2].map((i) => idxWrap(i, count));
        const nextFront = cur[1] ?? idxWrap((cur[0] ?? 0) + 1, count);
        const nextMid = cur[2] ?? idxWrap(nextFront + 1, count);
        const nextBack = idxWrap(nextMid + 1, count);
        setOutgoing({ index: cur[0] ?? 0, stage: 0 });
        requestAnimationFrame(() => setOutgoing((o) => (o ? { ...o, stage: 1 } : o)));
        window.setTimeout(() => setOutgoing(null), 520);
        return [nextFront, nextMid, nextBack];
      });
    }, intervalMs);
    return () => window.clearTimeout(t);
  }, [front, paused, intervalMs, count]);

  useEffect(() => {
    if (count <= 0) return;
    setDeck([0, 1, 2].map((i) => idxWrap(i, count)));
    setOutgoing(null);
    setProgress(0);
  }, [count]);

  const goTo = (idx: number) => {
    if (count <= 0) return;
    const nextFront = idxWrap(idx, count);
    const nextMid = idxWrap(nextFront + 1, count);
    const nextBack = idxWrap(nextFront + 2, count);
    setOutgoing(null);
    setDeck([nextFront, nextMid, nextBack]);
    startRef.current = performance.now();
    setProgress(0);
  };

  const goPrev = () => {
    if (count <= 1) return;
    setDeck((prev) => {
      const cur = prev.length ? prev : [0, 1, 2].map((i) => idxWrap(i, count));
      const prevFront = idxWrap((cur[0] ?? 0) - 1, count);
      const nextMid = cur[0] ?? 0;
      const nextBack = cur[1] ?? idxWrap(nextMid + 1, count);
      setOutgoing({ index: cur[0] ?? 0, stage: 0 });
      requestAnimationFrame(() => setOutgoing((o) => (o ? { ...o, stage: 1 } : o)));
      window.setTimeout(() => setOutgoing(null), 520);
      return [prevFront, nextMid, nextBack];
    });
  };

  const goNext = () => {
    if (count <= 1) return;
    setDeck((prev) => {
      const cur = prev.length ? prev : [0, 1, 2].map((i) => idxWrap(i, count));
      const nextFront = cur[1] ?? idxWrap((cur[0] ?? 0) + 1, count);
      const nextMid = cur[2] ?? idxWrap(nextFront + 1, count);
      const nextBack = idxWrap(nextMid + 1, count);
      setOutgoing({ index: cur[0] ?? 0, stage: 0 });
      requestAnimationFrame(() => setOutgoing((o) => (o ? { ...o, stage: 1 } : o)));
      window.setTimeout(() => setOutgoing(null), 520);
      return [nextFront, nextMid, nextBack];
    });
  };

  if (count === 0) {
    return (
      <div className="bg-[#10284a] border border-sky-300/30 rounded-2xl p-4 shadow-[0_24px_60px_-36px_rgba(2,8,23,0.85)]">
        <div className="h-[360px] rounded-xl bg-[#0b1f3f] border border-sky-300/25" />
      </div>
    );
  }

  const slideFront = safeSlides[front];
  const slideMid = safeSlides[mid];
  const slideBack = safeSlides[back];

  return (
    <div
      className="relative"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="relative h-[540px] sm:h-[590px]">
        {slideBack ? (
          <div className="hidden md:block absolute inset-0 transition-[transform,opacity] duration-500 ease-out transform translate-x-12 translate-y-5 scale-[0.955] opacity-50 pointer-events-none">
            <div className="h-full rounded-3xl bg-[#18365b] border border-sky-300/20 shadow-[0_34px_72px_-56px_rgba(2,8,23,0.95)] p-2.5">
              <SlideWindow slide={slideBack} showControls={false} onPrev={goPrev} onNext={goNext} disableControls={true} locale={locale} />
            </div>
          </div>
        ) : null}

        {slideMid ? (
          <div className="hidden sm:block absolute inset-0 transition-[transform,opacity] duration-500 ease-out transform translate-x-6 translate-y-2.5 scale-[0.98] opacity-75 pointer-events-none">
            <div className="h-full rounded-3xl bg-[#1d416d] border border-sky-300/25 shadow-[0_34px_72px_-54px_rgba(2,8,23,0.92)] p-3">
              <SlideWindow slide={slideMid} showControls={false} onPrev={goPrev} onNext={goNext} disableControls={true} locale={locale} />
            </div>
          </div>
        ) : null}

        {slideFront ? (
          <div className="absolute inset-0 transition-[transform,opacity] duration-500 ease-out transform translate-x-0 translate-y-0 scale-100 opacity-100">
            <div className="h-full rounded-3xl bg-[#10284a] border border-sky-300/30 shadow-[0_30px_82px_-52px_rgba(2,8,23,0.95)] p-3 sm:p-3.5 flex flex-col overflow-hidden">
              <div className="flex-1 min-h-0">
                <SlideWindow slide={slideFront} showControls={true} onPrev={goPrev} onNext={goNext} disableControls={count <= 1} locale={locale} />
              </div>

              <div className="mt-3 flex flex-col rounded-2xl bg-[#0b1f3f] border border-sky-300/18 px-5 py-4 sm:px-6">
                <div className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-[#d1a03c] text-[#1f2937] border border-[#e6bf6a] w-fit">
                  {slideFront.badge_text || (locale === 'ar' ? 'عرض المنتج' : 'Product Showcase')}
                </div>

                <div className="mt-4 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-white text-[17px] sm:text-lg font-bold leading-snug truncate">
                      {slideFront.title}
                    </p>
                    <p className="mt-2 text-slate-200 text-sm leading-relaxed line-clamp-2">
                      {slideFront.subtitle}
                    </p>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex items-center justify-between gap-4 sm:gap-6">
                    <div className="flex items-center gap-2">
                      {safeSlides.map((_, idx) => {
                        const isActive = idx === front;
                        const tGoTo = locale === 'ar' ? `الانتقال إلى الشريحة ${idx + 1}` : `Go to slide ${idx + 1}`;
                        return (
                          <button
                            key={`dot-${idx}`}
                            type="button"
                            className={`h-2.5 rounded-full transition-all ${isActive ? 'w-7 bg-[#d1a03c]' : 'w-2.5 bg-slate-400 hover:bg-slate-300'}`}
                            onClick={() => goTo(idx)}
                            aria-label={tGoTo}
                          />
                        );
                      })}
                    </div>
                    <div className="flex-1 max-w-[240px]">
                      <div className="h-1.5 rounded-full bg-slate-600 overflow-hidden">
                        <div
                          className="h-full bg-[#d1a03c]"
                          style={{ width: `${Math.round(progress * 100)}%`, transition: paused ? 'none' : 'width 120ms linear' }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {outgoing && safeSlides[outgoing.index] ? (
          <div
            className={`absolute inset-0 pointer-events-none transition-[transform,opacity] duration-500 ease-out ${
              outgoing.stage === 0
                ? 'translate-x-0 translate-y-0 scale-100 opacity-100'
                : 'translate-x-10 translate-y-5 scale-[0.92] opacity-0'
            }`}
          >
            <div className="h-full rounded-3xl bg-[#18365b] border border-sky-300/20 shadow-[0_30px_78px_-54px_rgba(2,8,23,0.95)] p-3.5 sm:p-4">
              <SlideWindow
                slide={safeSlides[outgoing.index]}
                showControls={false}
                onPrev={goPrev}
                onNext={goNext}
                disableControls={true}
                locale={locale}
              />
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
